import { Component } from 'react';
import mapboxgl from 'mapbox-gl';
import { pathToRegexp } from 'path-to-regexp';
import { useIntl } from '@alltrails/shared/react-intl';
import { additionalStyleCardConfigs, baseStyleCardConfigs } from '@alltrails/maps/utils/cardConfigs';
import { pointItemToLngLat } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import TrailHoverCard from '../../../../components/cards/TrailHoverCard';
import ActivityHoverCard from 'components/cards/ActivityHoverCard';
import WaypointPopup from '../../../../components/shared/MapPopups/WaypointPopup';
import { MapElevationPaneWidget } from '../search/fullscreen/map_elevation_pane_widget';
import { fitMapToBounds } from '../../../../utils/mapbox/map_helpers';
import { addAtMap } from '../../../../utils/mapbox/overlays/at_map';
import { addExploreItems } from '../../../../utils/mapbox/overlays/explore_items';
import { updateCurrLocMarker } from '../../../../utils/mapbox/overlays/current_location';
import { initCustomStyle, swapBaseStyle, createBlankStyle } from '../../../../utils/mapbox/style_helpers';
import { LanguageSupportUtil } from '../../../../utils/language_support_util';
import { initLayerEvents } from '../../../../utils/mapbox/layers/layer_helpers';
import CustomProvider from 'components/CustomProvider';
import baseStyleLoaders from 'utils/mapbox/baseStyleLoaders';
import { additionalStyleLoaders } from '../../../../utils/mapbox/additionalStyleLoaders';

class BaseMapWidget extends Component {
  constructor(props) {
    super(props);
    this.mbMap = null;
    this.state = {
      baseLayerKey: props.mapLayerDefault,
      showMenu: false
    };
  }

  componentDidMount() {
    this.initLegacyMapWidget();
    this.overlaysOn = {};
  }

  initLegacyMapWidget() {
    if (!mapboxgl.supported()) {
      alert(
        this.props.intl.formatMessage({
          defaultMessage:
            'It appears that your browser does not support MapboxGL, a library that is required to render maps on AllTrails.com. Please upgrade or change browsers before reloading the page. See https://docs.mapbox.com/help/troubleshooting/mapbox-browser-support/ for more details. Contact customer support with details about your device and browser if issue persists.'
        })
      );
      return;
    }

    const { context } = this.props;

    // Init mapbox map
    mapboxgl.accessToken = this.props.mapboxToken;
    const initCenter = pointItemToLngLat(this.props.trail || this.props.atMap) || undefined;
    const map = new mapboxgl.Map({
      center: initCenter,
      container: 'map',
      fadeDuration: 0,
      style: createBlankStyle(),
      zoom: 14
    });
    // Add custom AllTrails properites onto map
    map.context = context;
    initCustomStyle(map);
    initLayerEvents(map);
    // Add map onto state
    this.mbMap = map;

    if (!this.props.scrollWheelZoom) {
      this.mbMap.scrollZoom.disable();
    }

    // Switch to selected style
    this.mbMap.once('load', () => {
      const styleLoader = baseStyleLoaders[this.props.mapLayerDefault];
      styleLoader(context.locale, context.displayMetric, this.props.intl).then(style => {
        swapBaseStyle(map, style);
      });
    });

    // Add map controls
    map.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 80,
        unit: this.props.context.displayMetric ? 'metric' : 'imperial'
      }),
      'bottom-right'
    );
    // Render custom style onto map and adjust bounds
    map.once('load', () => {
      const { list, atMap } = this.props;
      const bounds = new mapboxgl.LngLatBounds();
      if (list) {
        addExploreItems(map, 'list', list, object => this.renderHoverCard(object), bounds);
      } else if (atMap) {
        const isMapCreatorPage = pathToRegexp('/:locale?/widget/map/:map').test(window?.location?.pathname);
        addAtMap({
          map,
          atMap,
          renderWaypointPopup: waypoint => this.renderWaypointPopup(waypoint),
          bounds,
          withWaypointLabels: false,
          isMapCreatorPage
        });
      }
      fitMapToBounds(map, bounds);
    });
  }

  zoomIn = () => {
    this.mbMap.zoomIn();
  };

  zoomOut = () => {
    this.mbMap.zoomOut();
  };

  toggleMenu = () => {
    this.setState(prevState => ({ showMenu: !prevState.showMenu }));
  };

  hideMenu = () => {
    this.setState({ showMenu: false });
  };

  switchLayer = key => {
    const { context } = this.props;
    const styleLoader = baseStyleLoaders[key];
    styleLoader(context.locale, context.displayMetric, this.props.intl).then(style => {
      swapBaseStyle(this.mbMap, style);
    });
    this.setState({ baseLayerKey: key });
  };

  toggleOverlay = key => {
    const styleLoader = additionalStyleLoaders[key];
    if (this.overlaysOn[key]) {
      styleLoader.remove(this.mbMap);
      delete this.overlaysOn[key];
    } else {
      const { atMap } = this.props;
      styleLoader.add(this.mbMap, { exploreMap: atMap });
      this.overlaysOn[key] = true;
    }
  };

  renderLayerRadio = key => {
    const checked = this.state.baseLayerKey === key;
    return (
      <input
        className="control"
        defaultChecked={checked}
        id={`menu-item-${key}`}
        name="map-layers"
        onClick={() => this.switchLayer(key)}
        type="radio"
        value={key}
      />
    );
  };

  renderOverlayCheckbox = key => {
    return (
      <input
        className="control styled-checkbox"
        id={`menu-item-${key}`}
        name="map-overlays"
        onClick={() => this.toggleOverlay(key)}
        type="checkbox"
        value={key}
      />
    );
  };

  renderMenu(title, keys, config, formElementFunc) {
    if (keys.length < 1) {
      return null;
    }
    const menuItems = keys.map(key => {
      return this.renderMenuItem(key, config[key], formElementFunc);
    });
    return (
      <>
        <div className="menu-title">{title}</div>
        <ul className="control">{menuItems}</ul>
      </>
    );
  }

  renderMenuItem(key, itemObject, formElementFunc) {
    return (
      <li key={`menu-item-li-${key}`}>
        {formElementFunc(key)}
        <label htmlFor={`menu-item-${key}`}>{itemObject.displayName}</label>
      </li>
    );
  }

  renderLinks() {
    const links = [];
    this.props.links.forEach((link, i) => {
      if (i > 0) {
        links.push(
          <span key={`${link.text} - delimiter`} className="delimiter">
            {' '}
            |{' '}
          </span>
        );
      }
      links.push(
        <span key={link.text} className="info">
          <a href={link.path} target="_blank" rel="noopener noreferrer">
            {link.text}
          </a>
        </span>
      );
    });
    return links;
  }

  renderTrailHoverCard(object) {
    const handleCardClick = () => {
      window.open(LanguageSupportUtil.wrapUrlSafe(`/${object.slug}`, this.props.context.languageRegionCode));
    };
    return <TrailHoverCard context={this.props.context} handleCardClick={handleCardClick} handleFavoriteClick={handleCardClick} trail={object} />;
  }

  renderTrackHoverCard(object) {
    // need to fully deserialize user (which is partially serialized when converted to a mapbox property)
    if (typeof object.user === 'string') {
      object = { ...object };
      object.user = JSON.parse(object.user);
    }
    const handleCardClick = () => {
      const url = `/explore/${object.type === 'track' ? 'recording' : 'map'}/${object.slug}`;
      window.open(LanguageSupportUtil.wrapUrlSafe(url, this.props.context.languageRegionCode));
    };
    return <ActivityHoverCard context={this.props.context} handleCardClick={handleCardClick} handleFavoriteClick={handleCardClick} track={object} />;
  }

  renderHoverCard(object) {
    if (object.type === 'trail') {
      return this.renderTrailHoverCard(object);
    }
    if (object.type === 'track' || object.type === 'map') {
      return this.renderTrackHoverCard(object);
    }
    return null;
  }

  renderWaypointPopup(waypoint) {
    const { id, location, name, description } = waypoint;
    waypoint = { id, location, name, description };
    // need to fully deserialize waypoint (which is partially serialized when converted to a mapbox property)
    if (typeof location === 'string') {
      waypoint.location = JSON.parse(location);
    }

    return <WaypointPopup waypoint={waypoint} />;
  }

  renderElevationChart() {
    if (!this.props.showElevationChart) {
      return null;
    }
    return (
      <div id="elevation">
        <MapElevationPaneWidget
          collapsed={false}
          displayMetric={this.props.context.displayMetric}
          languageRegionCode={this.props.context.languageRegionCode}
          elevationConfig={{
            areaColor: 'white',
            axisTextColor: 'gray',
            backgroundColor: 'white',
            endLineColor: 'blue',
            hoverLineColor: '#219AD8',
            lineColor: 'red',
            textColor: 'gray',
            tickColor: 'gray'
          }}
          handleMouseOver={position => {
            updateCurrLocMarker(this.mbMap, position);
          }}
          height={85}
          map={this.props.atMap}
          showStatsGroup={false}
          width={255}
        />
      </div>
    );
  }

  render() {
    return (
      <div id="static-map">
        <div id="controls">
          <div id="titleBar">
            <a className="logo" href={this.props.alltrailsLink} target="_blank" rel="noopener noreferrer">
              <img alt="AllTrails" src="https://cdn-assets.alltrails.com/assets/shared/alltrails_logo_widget.png" />
            </a>
            <div className="info-bar xlate-none">{this.renderLinks()}</div>
          </div>
          <div id="map-controls">
            {this.renderElevationChart()}
            <div className="left zoom-group">
              <button id="zoom-in" className="zoom control" onClick={this.zoomIn} type="button">
                <span>
                  <img alt="Zoom In" src="//cdn-assets.alltrails.com/assets/icons/maps/zoom-darkgreen.png" />
                </span>
              </button>
              <button id="zoom-out" className="zoom control" onClick={this.zoomOut} type="button">
                <span>
                  <img alt="Zoom Out" src="//cdn-assets.alltrails.com/assets/icons/maps/unzoom-darkgreen.png" />
                </span>
              </button>
            </div>
            <div className="right control-group">
              <div className="map-control-container">
                <button className="flyout-button-container control" onClick={this.toggleMenu} type="button">
                  <img alt="Map Options" className="layer-image" src="//cdn-assets.alltrails.com/assets/icons/trail/map-layers-dark-green.png" />
                </button>
                <div className={`${this.state.showMenu ? '' : 'hidden'} menu map-controls-menu`}>
                  {this.renderMenu(this.props.mapLayersTitle, this.props.mapLayers, baseStyleCardConfigs, this.renderLayerRadio)}
                  {this.renderMenu(this.props.mapOverlaysTitle, this.props.mapOverlays, additionalStyleCardConfigs, this.renderOverlayCheckbox)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="map" onClick={this.hideMenu} />
      </div>
    );
  }
}

const MapWidgetIntlProvider = props => {
  const intl = useIntl();
  return <BaseMapWidget {...props} intl={intl} />;
};

const MapWidget = props => {
  return (
    <CustomProvider>
      <MapWidgetIntlProvider {...props} />
    </CustomProvider>
  );
};

export { MapWidget };
